package com.merkost.handpass.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.merkost.handpass.admin.auth.AdminAuthGate
import com.merkost.handpass.admin.auth.LocalAdminAuth
import com.merkost.handpass.admin.auth.LocalThemeSetter
import com.merkost.handpass.admin.di.startAdminKoin
import com.merkost.handpass.sharedweb.theme.ThemeMode
import com.varabyte.kobweb.core.App
import com.varabyte.kobweb.silk.SilkApp
import com.varabyte.kobweb.silk.setStyleFor
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.EventListener
import pro.respawn.flowmvi.compose.dsl.ProvideSubscriberLifecycle
import pro.respawn.flowmvi.dsl.ImmediateLifecycle

val LocalThemeMode = compositionLocalOf { ThemeMode.SYSTEM }

private var koinStarted = false

@App
@Composable
fun AppEntry(content: @Composable () -> Unit) {
    if (!koinStarted) {
        startAdminKoin()
        koinStarted = true
    }
    var themeMode by remember { mutableStateOf(loadThemeMode()) }
    var systemPrefersDark by remember { mutableStateOf(systemPrefersDark()) }
    val resolvedMode = when (themeMode) {
        ThemeMode.SYSTEM -> if (systemPrefersDark) ThemeMode.DARK else ThemeMode.LIGHT
        else -> themeMode
    }

    DisposableEffect(Unit) {
        val query = window.matchMedia("(prefers-color-scheme: dark)")
        val listener = EventListener { systemPrefersDark = query.matches }
        query.addEventListener("change", listener)
        onDispose { query.removeEventListener("change", listener) }
    }

    SilkApp {
        LaunchedEffect(Unit) {
            injectBaseGuard()
            injectGoogleFonts()
            injectReducedMotionGuard()
        }
        val colorModeState = ColorMode.currentState
        LaunchedEffect(resolvedMode) {
            colorModeState.value = if (resolvedMode == ThemeMode.DARK) ColorMode.DARK else ColorMode.LIGHT
        }
        ProvideSubscriberLifecycle(ImmediateLifecycle) {
            CompositionLocalProvider(
                LocalThemeMode provides themeMode,
                LocalThemeSetter provides { newMode ->
                    themeMode = newMode
                    saveThemeMode(newMode)
                    val resolved = when (newMode) {
                        ThemeMode.SYSTEM -> if (systemPrefersDark) ThemeMode.DARK else ThemeMode.LIGHT
                        else -> newMode
                    }
                    colorModeState.value = if (resolved == ThemeMode.DARK) ColorMode.DARK else ColorMode.LIGHT
                },
            ) {
                AdminAuthGate { authState ->
                    CompositionLocalProvider(LocalAdminAuth provides authState) {
                        content()
                    }
                }
            }
        }
    }
}

private fun loadThemeMode(): ThemeMode {
    val raw = window.localStorage.getItem("admin:theme") ?: return ThemeMode.SYSTEM
    return runCatching { ThemeMode.valueOf(raw) }.getOrDefault(ThemeMode.SYSTEM)
}

private fun saveThemeMode(mode: ThemeMode) {
    window.localStorage.setItem("admin:theme", mode.name)
}

private fun systemPrefersDark(): Boolean =
    window.matchMedia("(prefers-color-scheme: dark)").matches
